import React from "react";

import { ErrorLogGroupInfo, ErrorLogItem, ErrorLogItems } from "../../../../../Engine/Core/ErrorManager/Utils/Models";
import { ErrorsItemContainer as ErrorsItem } from "../ErrorItem/ErrorsItemContainer";
import { ErrorSeverity } from "../ErrorSeverity";
import { isErrorCountExceeds, getCountLimit } from "../../ErrorLogModal/Utils/Utils";
import { BindingPath } from "../../../../../Engine/Core/CoreTypes";
import { ICandyComponentHandle } from "../../../../../Engine/Core/ComponentHandleRegistry/ICandyComponentHandle";
import { clamp } from "../../../../../../../Common/IntRangeUtils";
import { declint } from "../../../../../Engine/utils";
import { IconWrapper, ListItem, ListItemWrapper } from "../ErrorItem/ErrorsItem.styles";
import { ErrorItemIcon, ErrorItemIconStatus } from "../ErrorItem/ErrorItemIcon";
import { DEFAULT_SUCCESS_DESCRIPTION } from "../../../../../Engine/Core/ErrorManager/Utils/Utils";

import { ErrorGroupHeaderSticky } from "./ErrorGroupHeaderSticky";
import { GroupWrapper, LimitReachedMessage } from "./ErrorsGroup.styles";

interface ErrorsGroupProps {
    tid?: string;
    group: ErrorLogGroupInfo;
    errors?: ErrorLogItems;
    warnings?: ErrorLogItems;
    successes?: ErrorLogItems;
    isMainGroup: boolean;
    showSuccesses: boolean;
    range?: [start: number, end: number];
    showJustification?: boolean;
    useExtendedErrorLog?: boolean;
    onBeforeNavigateToControl?: () => void;
    onFindAndFocusControl: (
        paths: BindingPath[],
        highlanderPath?: string | null,
        errorItemId?: string
    ) => Promise<ICandyComponentHandle | undefined>;
    disableControlsTooltip: (bindingPaths: BindingPath[], errorId: string) => void;
    enableControlsTooltip: () => void;
    setIsTargetEdited: (isTargetEdited: boolean) => void;
    setAnchorElement: React.Dispatch<React.SetStateAction<HTMLElement | undefined>>;
    additionalHeaderOffset?: number;
}

export const ErrorsGroup = (props: ErrorsGroupProps) => {
    const countLimit = getCountLimit();
    const groupErrorsCount = props.group.errorIds.length;
    const groupWarningsCount = props.group.warningIds.length;
    const groupSuccessesCount = props.group.successIds.length;

    const renderErrorLogMessage = () => {
        if (props.useExtendedErrorLog) {
            if (isErrorCountExceeds(groupErrorsCount)) {
                return (
                    <LimitReachedMessage>
                        Проверка остановлена на {countLimit} ошибке. Исправьте ошибки и повторите проверку.
                    </LimitReachedMessage>
                );
            }
            if (props.range != undefined) {
                return null;
            }
            if (groupErrorsCount + groupWarningsCount > countLimit) {
                return (
                    <LimitReachedMessage>
                        Показано {countLimit} первых ошибок и предупреждений. <br />
                        Чтобы посмотреть все, откройте подробный результат проверки.
                    </LimitReachedMessage>
                );
            }
        }
        if (groupErrorsCount + groupWarningsCount > countLimit) {
            return (
                <LimitReachedMessage>
                    Показано {countLimit} первых ошибок и предупреждений. <br />
                    Исправьте ошибки и повторите проверку.
                </LimitReachedMessage>
            );
        }
        return null;
    };

    const handleFindAndFocusControl = async (
        paths: BindingPath[],
        errorItem: ErrorLogItem,
        highlanderPath?: string | null
    ) => {
        props.onBeforeNavigateToControl?.();
        const target = await props.onFindAndFocusControl(paths, highlanderPath, errorItem.id);
        if (target != undefined) {
            props.setAnchorElement(target.getAnchorElement());
            props.setIsTargetEdited(false);
            props.disableControlsTooltip(paths, errorItem.id);
        } else {
            resetTarget();
        }
    };

    const resetTarget = () => {
        props.enableControlsTooltip();
        props.setAnchorElement(undefined);
        props.setIsTargetEdited(false);
    };

    const slicedErrorIds = props.isMainGroup
        ? props.group.errorIds.slice(props.range?.[0] ?? 0, props.range?.[1] ?? Infinity).slice(0, countLimit)
        : props.group.errorIds;
    const errorItems = slicedErrorIds.map(id => {
        const error = props.errors?.[id];
        if (!error) {
            return null;
        }
        return (
            <ErrorsItem
                showJustification={props.showJustification}
                key={error.id}
                error={error}
                type={ErrorSeverity.Error}
                onActivateItem={(paths, highlanderPath) => handleFindAndFocusControl(paths, error, highlanderPath)}
            />
        );
    });

    const slicedWarningIds = props.isMainGroup
        ? props.group.warningIds.slice(
              props.range == undefined ? 0 : clamp(props.range[0] - groupErrorsCount, 0, Infinity),
              props.range == undefined
                  ? clamp(countLimit - groupErrorsCount, 0, countLimit)
                  : clamp(props.range[1] - groupErrorsCount, 0, Infinity)
          )
        : props.group.warningIds;
    const warningItems = slicedWarningIds.map(id => {
        const warning = props.warnings?.[id];
        if (!warning) {
            return null;
        }
        return (
            <ErrorsItem
                showJustification={props.showJustification}
                key={warning.id}
                error={warning}
                type={ErrorSeverity.Warning}
                onActivateItem={(paths, highlanderPath) => handleFindAndFocusControl(paths, warning, highlanderPath)}
            />
        );
    });

    const slicedSuccessIds = props.isMainGroup
        ? props.group.successIds.slice(
              props.range == undefined ? 0 : clamp(props.range[0] - groupErrorsCount - groupWarningsCount, 0, Infinity),
              props.range == undefined
                  ? Infinity
                  : clamp(props.range[1] - groupErrorsCount - groupWarningsCount, 0, Infinity)
          )
        : props.group.successIds;
    const renderSuccessItems = () =>
        slicedSuccessIds.map(id => {
            const success = props.successes?.[id];
            if (!success) {
                return null;
            }
            return (
                <ErrorsItem
                    showJustification={props.showJustification}
                    key={success.id}
                    error={success}
                    type={ErrorSeverity.Success}
                    onActivateItem={(paths, highlanderPath) =>
                        handleFindAndFocusControl(paths, success, highlanderPath)
                    }
                />
            );
        });

    const renderSuccessItemCount = () => (
        <ListItemWrapper>
            <ListItem>
                <IconWrapper>
                    <ErrorItemIcon status={ErrorItemIconStatus.Success} />
                </IconWrapper>
                <div>
                    {declint(groupSuccessesCount, ["%s проверка", "%s проверки", "%s проверок"])}{" "}
                    {declint(groupSuccessesCount, ["прошла", "прошли", "прошли"])} успешно
                </div>
            </ListItem>
        </ListItemWrapper>
    );

    const renderDefaultSuccessMessage = () => (
        <ListItemWrapper>
            <ListItem>
                <IconWrapper>
                    <ErrorItemIcon status={ErrorItemIconStatus.Success} />
                </IconWrapper>
                <div>{DEFAULT_SUCCESS_DESCRIPTION}</div>
            </ListItem>
        </ListItemWrapper>
    );

    const showSuccessItems = () => {
        if (!props.useExtendedErrorLog) {
            if (groupErrorsCount === 0 && groupWarningsCount === 0) {
                return renderDefaultSuccessMessage();
            }
            return null;
        }
        if (isErrorCountExceeds(groupErrorsCount)) {
            return null;
        }
        if (!props.showSuccesses && (groupErrorsCount > 0 || groupWarningsCount > 0)) {
            return null;
        }
        if (props.showSuccesses) {
            if (groupErrorsCount === 0 && groupWarningsCount === 0 && groupSuccessesCount === 0) {
                return renderDefaultSuccessMessage();
            }
            return renderSuccessItems();
        }
        if (groupSuccessesCount > 0) {
            return renderSuccessItemCount();
        }
        return renderDefaultSuccessMessage();
    };

    const successItems = showSuccessItems();

    return (
        <GroupWrapper data-tid={props.group.tid}>
            <ErrorGroupHeaderSticky
                title={props.group.groupName}
                additionalHeaderOffset={props.additionalHeaderOffset}
            />
            {errorItems}
            {warningItems}
            {successItems}
            {props.isMainGroup && renderErrorLogMessage()}
        </GroupWrapper>
    );
};
